import { parseCalendarDate } from './calendarDate'

// How old a comp is, and which date says so. Moved out of columns.js by mr-02 unchanged, because
// the Time filter now has to answer the same question the Date column prints.
//
// Two callers, one function, deliberately. If the filter wrote its own months-ago arithmetic the
// screen could show a row reading `6.7 mo` that a 6-month bound had kept — the same shape as the
// displayOrder defect in reilm-be utils/arvFetch.js, where one number computed twice drifted. The
// date each comp is measured from is part of that: a Sold comp is dated by its sale and everything
// else by its listing, and getting that wrong for one caller would filter on a date the table never
// shows.
const MS_PER_DAY = 1000 * 60 * 60 * 24
const DAYS_PER_MONTH = 30.44

// Sold is dated by the sale, Active and Pending by the listing. Same expression the Date column has
// always used.
export function compDate(row) {
  return row?.status === 'Sold' ? row?.last_sold_date : row?.list_date
}

// Date, one row. Two requirements, both his, both literal: "Remove second row value of Date column"
// and "Add \"27 d\" (x d) date formatting if Date value is less than 1 month".
//
// The unit is what changes, not just the wording: under a month the age reads in whole days, over it
// in months to one decimal. "0.9 mo" is the string this replaces, and a comp listed three weeks ago
// is exactly the row an investor looks at hardest.
//
// The boundary is one month expressed as 30.44 days, the same average month the months figure is
// built on — using 30 or 31 here would leave an age that rounds to "1.0 mo" while still printing in
// days, or the reverse.
export function compAge(dateStr) {
  if (!dateStr) return null
  const ms = Date.now() - parseCalendarDate(dateStr).getTime()
  if (!Number.isFinite(ms)) return null
  const days = ms / MS_PER_DAY
  if (days < DAYS_PER_MONTH) return { value: String(Math.max(0, Math.round(days))), unit: 'd' }
  return { value: (days / DAYS_PER_MONTH).toFixed(1), unit: 'mo' }
}

// The unrounded age in months, for the Time bound. Not derived from compAge's string: that one
// rounds to one decimal for display, so a comp at 12.04 months would print `12.0 mo` and a bound of
// 12 has to decide about the real number rather than about what the cell says. Returns null when
// there is no date at all, which the caller reads as "unknown", never as "old" — the same rule the
// price and sqft bounds already follow in Arv.jsx.
export function compAgeMonths(row) {
  const dateStr = compDate(row)
  if (!dateStr) return null
  const ms = Date.now() - parseCalendarDate(dateStr).getTime()
  if (!Number.isFinite(ms)) return null
  return ms / MS_PER_DAY / DAYS_PER_MONTH
}
